import axios from 'axios'
import {
    ANIME_TYPES,
    API,
    SEARCH_TIMEOUT,
    TOKEN,
    dedupe,
    fetchKodikTitle,
    searchAnime,
    searchAnimeByShikimoriId,
} from '../../store/API/Kodik.js'
import { formatAnimeReleaseMeta } from '../../utils/animeRelease.js'
import { toNullableNumber, uniqueBy } from '../../utils/media.js'

function animePoster(item) {
    let material = item.material_data || {}
    return material.poster_url || material.anime_poster_url || null
}

function normalizeAnime(item) {
    let id = item && item.id
    if (!id) return null
    let material = item.material_data || {}
    let releaseType = String(material.anime_kind || material.all_kind || '').toLowerCase()
    let episodes = toNullableNumber(material.episodes_total || item.episodes_count || item.last_episode)
    let isEpisodic = ['movie', 'film'].includes(releaseType)
        ? false
        : item.type === 'anime-serial' || episodes > 1 || ['tv', 'ona', 'ova'].includes(releaseType)
    return {
        key: `kodik:${id}`,
        provider: 'kodik',
        id: String(id),
        kind: 'anime',
        title: item.title || item.title_orig || 'Без названия',
        originalTitle: item.title_orig || '',
        year: item.year || null,
        yearEnd: null,
        poster: animePoster(item),
        subtitle: formatAnimeReleaseMeta(item),
        releaseType,
        isEpisodic,
        ids: {
            kinopoisk: item.kinopoisk_id || null,
            imdb: item.imdb_id || null,
            tmdb: item.material_data && item.material_data.tmdb_id || null,
            shikimori: item.shikimori_id || null,
        },
        playbackRef: { provider: 'kodik', src: item.link || '' },
    }
}

function timestamp(value) {
    let parsed = value ? new Date(value).getTime() : NaN
    return Number.isFinite(parsed) ? parsed : null
}

function boundedPositiveInteger(value, fallback, maximum) {
    let parsed = Number(value)
    if (!Number.isFinite(parsed)) return fallback
    return Math.max(1, Math.min(maximum, Math.floor(parsed)))
}

function discoveryYears(values, now) {
    let supplied = (Array.isArray(values) ? values : [])
        .map(Number)
        .filter((value) => Number.isInteger(value) && value >= 1900 && value <= 2200)
    let unique = [...new Set(supplied)].sort((first, second) => first - second)
    if (unique.length) return unique
    let currentYear = new Date(now).getUTCFullYear()
    return Array.from({ length: 7 }, (_, index) => currentYear - 6 + index)
}

function animeStatus(value) {
    return {
        anons: 'announced',
        ongoing: 'ongoing',
        released: 'released',
    }[String(value || '').toLowerCase()] || 'unknown'
}

function normalizeAnimeCandidate(item, index) {
    let media = normalizeAnime(item)
    if (!media) return null
    let material = item.material_data || {}
    let screenshots = [
        ...(Array.isArray(material.screenshots) ? material.screenshots : []),
        ...(Array.isArray(item.screenshots) ? item.screenshots : []),
    ].map((value) => String(value || '').trim()).filter((value) => /^https?:\/\//i.test(value))
    let episodes = toNullableNumber(material.episodes_total || item.episodes_count || item.last_episode)
    let status = animeStatus(material.anime_status || material.all_status)

    return {
        source: 'kodik-recent',
        media: {
            ...media,
            description: material.description || material.anime_description || '',
            episodes,
            releaseStatus: status,
            backdropCandidates: [...new Set([...screenshots, media.poster].filter(Boolean))].slice(0, 4),
        },
        signals: {
            ratings: {
                kinopoisk: toNullableNumber(material.kinopoisk_rating),
                imdb: toNullableNumber(material.imdb_rating),
                shikimori: toNullableNumber(material.shikimori_rating),
            },
            votes: {
                kinopoisk: toNullableNumber(material.kinopoisk_votes),
                imdb: toNullableNumber(material.imdb_votes),
                shikimori: toNullableNumber(material.shikimori_votes),
            },
            popularity: null,
            premiereAt: material.aired_at || material.released_at || material.premiere_world || null,
            updatedAt: timestamp(item.updated_at),
            status,
            playbackQuality: String(item.quality || '').trim() || null,
            sourceRank: index,
        },
    }
}

function mediaUrls(values) {
    return uniqueBy((values || [])
        .map((url) => String(url || '').trim())
        .filter((url) => /^https?:\/\//i.test(url))
        .map((url) => ({ url, width: null, height: null })), (image) => image.url)
        .slice(0, 12)
}

function normalizeKodikDetails(item) {
    let summary = normalizeAnime(item)
    if (!summary) return null
    let material = item.material_data || {}
    const editorialScreenshots = mediaUrls(material.screenshots || [])
    const playerScreenshots = mediaUrls(item.screenshots || [])
    const screenshots = uniqueBy([
        ...editorialScreenshots,
        ...playerScreenshots,
    ], (image) => image.url).slice(0, 12)
    let genres = material.anime_genres || material.all_genres || []
    let countries = material.countries || []
    let statusNames = { anons: 'Анонс', ongoing: 'В работе', released: 'Завершён' }
    let durationMatch = String(material.duration || '').match(/\d+/)

    return {
        ...summary,
        description: material.description || material.anime_description || '',
        slogan: '',
        durationMinutes: durationMatch ? Number(durationMatch[0]) : null,
        ageRating: '',
        premiere: '',
        ratings: {
            kinopoisk: toNullableNumber(material.kinopoisk_rating),
            imdb: toNullableNumber(material.imdb_rating),
        },
        genres: Array.isArray(genres) ? genres.filter(Boolean) : String(genres).split(',').map((item) => item.trim()).filter(Boolean),
        countries: Array.isArray(countries) ? countries.filter(Boolean) : [],
        directors: [],
        actors: [],
        episodes: toNullableNumber(material.episodes_total || item.episodes_count || item.last_episode),
        status: statusNames[material.anime_status || material.all_status] || '',
        updatedAt: item.updated_at ? Math.floor(new Date(item.updated_at).getTime() / 1000) : null,
        images: { posters: [], backdrops: [], stills: screenshots },
        /* Player screenshots are compressed episode frames. They stay in the
           details gallery but never become a random full-width home hero. */
        backdrop: summary.poster,
        backdropCandidates: summary.poster
            ? [{ url: summary.poster, width: null, height: null }]
            : [],
    }
}

async function findByReference(reference, signal) {
    if (reference.provider === 'kodik' && reference.providerId) {
        try {
            let hinted = await fetchKodikTitle(reference.providerId, { signal })
            let material = hinted && hinted.material_data || {}
            let hintedId = {
                shikimori: hinted && hinted.shikimori_id,
                kinopoisk: hinted && hinted.kinopoisk_id,
                imdb: hinted && hinted.imdb_id,
                tmdb: material.tmdb_id,
            }[reference.source]
            if (
                reference.source === 'provider'
                || (hinted && String(hintedId || '') === String(reference.id))
            ) return hinted
        } catch (error) {
            if (signal && signal.aborted) throw error
        }
    }
    if (reference.source === 'provider' && reference.provider === 'kodik') {
        return fetchKodikTitle(reference.id, { signal })
    }
    if (reference.source === 'shikimori') {
        let results = await searchAnimeByShikimoriId(reference.id, { signal })
        return results[0] || null
    }

    let parameter = {
        kinopoisk: 'kinopoisk_id',
        imdb: 'imdb_id',
        tmdb: 'tmdb_id',
    }[reference.source]
    if (!parameter) return null
    let response = await axios.get(`${API}/search`, {
        params: {
            token: TOKEN,
            types: ANIME_TYPES,
            [parameter]: reference.id,
            limit: 100,
            with_material_data: true,
            with_episodes_data: true,
        },
        signal,
        timeout: SEARCH_TIMEOUT,
    })
    return response.data && response.data.results && response.data.results[0] || null
}

export const kodikProvider = {
    id: 'kodik',
    capabilities: Object.freeze({
        anime: true,
        movies: false,
        series: false,
        search: true,
        details: true,
        iframe: true,
        structuredEpisodes: true,
        structuredTranslations: true,
    }),
    async discoverRecentAnime({ signal, limit = 100, years, now = Date.now() } = {}) {
        let pageSize = boundedPositiveInteger(limit, 100, 100)
        let response = await axios.get(`${API}/list`, {
            params: {
                token: TOKEN,
                types: ANIME_TYPES,
                year: discoveryYears(years, now).join(','),
                sort: 'updated_at',
                order: 'desc',
                limit: pageSize,
                with_material_data: true,
            },
            signal,
            timeout: SEARCH_TIMEOUT,
        })
        return dedupe(response.data && response.data.results)
            .map(normalizeAnimeCandidate)
            .filter(Boolean)
    },
    async search(query, { signal, compact } = {}) {
        let results = await searchAnime(query, { signal, compact })
        return results.map(normalizeAnime).filter(Boolean)
    },
    async getDetails(id, { signal } = {}) {
        return normalizeKodikDetails(await fetchKodikTitle(id, { signal }))
    },
    async resolve(reference, { signal } = {}) {
        return normalizeKodikDetails(await findByReference(reference, signal))
    },
    getPlayer(media) {
        return {
            provider: 'kodik',
            mode: 'iframe',
            src: media && media.playbackRef && media.playbackRef.src || '',
            title: media && media.title || 'Kodik',
        }
    },
}

export {
    normalizeAnime as normalizeKodikAnime,
    normalizeAnimeCandidate as normalizeKodikAnimeCandidate,
    normalizeKodikDetails,
}
